import Appointment from "../interface/appointment";
import mapAppointmentToExportRow, {
  AppointmentExportRow,
} from "./mapAppointmentToExportRow";

// Column definitions in display order. The header doubles as the row key, and
// typing it as `keyof AppointmentExportRow` makes the compiler reject a header
// that no export row actually fills — exceljs would silently leave such a
// column empty.
const COLUMNS: { header: keyof AppointmentExportRow; width: number }[] = [
  { header: "Datum", width: 12 },
  { header: "Uhrzeit", width: 16 },
  { header: "Typ", width: 16 },
  { header: "Teilnehmer:in", width: 26 },
  { header: "Coach:in", width: 24 },
  { header: "Status", width: 12 },
  { header: "Telefon", width: 18 },
  { header: "E-Mail", width: 28 },
  { header: "Standort", width: 18 },
  { header: "Qualifikation", width: 24 },
];

/**
 * Builds an .xlsx workbook from the given appointments and returns it as an
 * ArrayBuffer ready to be downloaded. The header row is styled, the Datum column
 * keeps a real date value (displayed as dd.mm.yyyy) and Telefon is written as
 * text so leading zeros / "+" are preserved.
 */
const buildAppointmentsWorkbook = async (
  appointments: Appointment[],
): Promise<ArrayBuffer> => {
  // Lazily import exceljs so its (large) bundle is only fetched when a user
  // actually exports, keeping it out of the main app bundle.
  const { default: ExcelJS } = await import("exceljs");

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Terminübersicht");

  worksheet.columns = COLUMNS.map(({ header, width }) => ({
    header,
    key: header,
    width,
  }));

  // Style the header row.
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: "middle" };

  appointments
    .map(mapAppointmentToExportRow)
    .forEach((row) => worksheet.addRow(row));

  // Display the date column as German dd.mm.yyyy while keeping it a real date.
  worksheet.getColumn("Datum").numFmt = "dd.mm.yyyy";
  // Keep phone numbers as text so leading zeros and "+" survive.
  worksheet.getColumn("Telefon").numFmt = "@";

  // Enable Excel's autofilter across the populated header columns.
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: COLUMNS.length },
  };

  return workbook.xlsx.writeBuffer();
};

export default buildAppointmentsWorkbook;
